import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    ComposedChart,
    Line,
    Legend,
} from 'recharts';
import { api } from '../api/client.js';
import { formatCOP, formatCOPAllowZero } from '../utils/money.js';
import { capabilitiesFromUser } from '../utils/solutionCapabilities.js';
import './Calculator.css';

const IMPACT_CHART_COLORS = ['#dc2626', '#f97316', '#ca8a04', '#6366f1'];
const MESES_CORTO_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function mesEtiquetaEs(yyyyMm) {
    if (!yyyyMm || !yyyyMm.includes('-')) return yyyyMm || '';
    const [y, m] = yyyyMm.split('-');
    const idx = parseInt(m, 10) - 1;
    if (idx < 0 || idx > 11) return yyyyMm;
    return `${MESES_CORTO_ES[idx]} ${y}`;
}

function expandirTendenciaAnual(monthlyTrend) {
    const year = new Date().getFullYear();
    const mesActual = new Date().getMonth() + 1;
    const porMes = new Map((monthlyTrend || []).map((r) => [r.month, r]));
    const filas = [];
    for (let m = 1; m <= mesActual; m += 1) {
        const clave = `${year}-${String(m).padStart(2, '0')}`;
        filas.push(porMes.get(clave) || { month: clave, events: 0, cost: 0 });
    }
    return filas;
}

function construirSerieRotacionAnual(monthlyTrend) {
    let acumulado = 0;
    return expandirTendenciaAnual(monthlyTrend).map((row) => {
        const costoMes = Number(row.cost) || 0;
        acumulado += costoMes;
        return {
            mes: mesEtiquetaEs(row.month),
            costoMes,
            acumulado,
            bajas: Number(row.events) || 0,
        };
    });
}

const CID_LEVEL_ORDER = ['operativo', 'tecnico', 'supervision'];
const CID_LEVEL_LABEL = {
    operativo: 'Operativo base',
    tecnico: 'Técnico especializado',
    supervision: 'Supervisión / Liderazgo',
};
/** Valores sugeridos si aún no hay fila en BD (misma lógica que el seed demo). */
const CID_DEFAULTS_BY_LEVEL = {
    operativo: { recruitmentCost: 42000, severanceCost: 58000, ppeCost: 9000, adminHoursCost: 16000, learningCurveMonths: 4.5, gap: 0.52, masteryFactor: 0.48 },
    tecnico: { recruitmentCost: 88000, severanceCost: 125000, ppeCost: 18500, adminHoursCost: 34000, learningCurveMonths: 6.2, gap: 0.46, masteryFactor: 1.05 },
    supervision: { recruitmentCost: 155000, severanceCost: 230000, ppeCost: 28000, adminHoursCost: 62000, learningCurveMonths: 10, gap: 0.38, masteryFactor: 1.85 },
};

function inferPositionLevel(emp) {
    if (emp.role === 'supervisor' || emp.role === 'admin') return 'supervision';
    const pos = String(emp.position || '').toLowerCase();
    if (
        pos.includes('técnic') ||
        pos.includes('tecnico') ||
        pos.includes('especial') ||
        pos.includes('ingenier')
    ) {
        return 'tecnico';
    }
    return 'operativo';
}

function riskSignalsForWorker(emp) {
    if (emp.role !== 'worker') return [];
    const out = [];
    if (emp.latestFeeling === 'mal') out.push('Último check-in en “mal”');
    if (emp.latestFeeling === 'sick') out.push('Último check-in: indispuesto');
    if (emp.latestFeeling === 'regular') out.push('Último check-in en “regular” (vigilar)');
    const alerts = emp.activeAlerts || [];
    for (const a of alerts) {
        const n = Number(a.count) || 0;
        if (a.level === 'red' && n > 0) out.push(`${n} alerta(s) roja(s) activa(s)`);
        if (a.level === 'yellow' && n >= 2) out.push(`${n} alertas amarillas (patrón elevado)`);
    }
    return out;
}

async function resolveWorkerSalaryPayload(emp, tenantId) {
    if (!emp?.area_id) return null;
    const level = inferPositionLevel(emp);
    let salary = 0;
    let cargo = '';
    let byCargo = [];
    let cohortAverage = null;
    let cohortCount = 0;
    let usedTechnicoWorkerFallback = false;

    try {
        const data = await api.getCalculatorSalaryAverages(emp.area_id, level, tenantId);
        byCargo = data.byCargo || [];
        cohortAverage = data.cohortAverage ?? null;
        cohortCount = data.cohortCount ?? 0;
        usedTechnicoWorkerFallback = !!data.usedTechnicoWorkerFallback;
        const posLabel = String(emp.position || '').trim();
        const empSalary = Number(emp.monthly_salary);

        if (empSalary > 0) {
            salary = Math.round(empSalary);
            const match =
                byCargo.find((r) => r.cargo === posLabel) ||
                (posLabel ? byCargo.find((r) => r.cargo.includes(posLabel) || posLabel.includes(r.cargo)) : null);
            cargo = match?.cargo || byCargo[0]?.cargo || '';
        } else if (byCargo.length) {
            const match = posLabel ? byCargo.find((r) => r.cargo === posLabel) : null;
            const row = match || byCargo[0];
            cargo = row.cargo;
            salary = Number(row.avgMonthlySalary ?? row.avgmonthlysalary ?? 0);
        } else if (cohortAverage != null && Number(cohortAverage) > 0) {
            salary = Math.round(Number(cohortAverage));
        }
    } catch {
        const empSalary = Number(emp.monthly_salary);
        if (empSalary > 0) salary = Math.round(empSalary);
    }

    if (salary <= 0) return null;

    return {
        areaId: Number(emp.area_id),
        positionLevel: level,
        monthlySalary: salary,
        cargo,
        salaryContext: {
            byCargo,
            cohortAverage,
            cohortCount,
            usedTechnicoWorkerFallback,
        },
    };
}

function mergeSimulationResults(entries) {
    if (!entries?.length) return null;
    const merged = {
        scenarioType: entries[0].simulation.scenarioType || 'rotacion',
        quantity: entries.length,
        aggregated: entries.length > 1,
        selectedWorkers: entries.map(({ emp, simulation: s }) => ({
            id: emp.id,
            name: emp.full_name,
            areaName: emp.area_name,
            impact: s.totalImpact,
        })),
        supervisorName:
            [...new Set(entries.map((e) => e.simulation.supervisorName).filter((n) => n && n !== 'No asignado'))].join(
                ' · '
            ) || entries[0].simulation.supervisorName,
        destabilizationApplied:
            entries.length > 1
                ? 'Suma individual por área y nivel'
                : entries[0].simulation.destabilizationApplied,
        directCosts: { recruitment: 0, severance: 0, ppe: 0, adminHours: 0, total: 0 },
        productivityLost: {
            total: 0,
            monthlySalary: 0,
            learningCurveMonths: entries[0].simulation.productivityLost.learningCurveMonths,
            gapPercentage: entries[0].simulation.productivityLost.gapPercentage,
        },
        systemicFriction: {
            total: 0,
            annualSalary: 0,
            baseMasteryFactor: entries[0].simulation.systemicFriction.baseMasteryFactor,
            biometricRiskMultiplier: entries[0].simulation.systemicFriction.biometricRiskMultiplier,
            adjustedMasteryFactor: entries[0].simulation.systemicFriction.adjustedMasteryFactor,
            status: entries.some((e) => e.simulation.systemicFriction.status === 'Señales elevadas (simulación)')
                ? 'Señales elevadas (simulación)'
                : 'Estable',
        },
        totalImpact: 0,
        infancyCost: 0,
        costsEstimated: entries.some((e) => e.simulation.costsEstimated),
        disclaimer: entries[0].simulation.disclaimer,
    };

    let salarySum = 0;
    let salaryCount = 0;
    for (const { simulation: s } of entries) {
        merged.directCosts.recruitment += s.directCosts.recruitment;
        merged.directCosts.severance += s.directCosts.severance;
        merged.directCosts.ppe += s.directCosts.ppe;
        merged.directCosts.adminHours += s.directCosts.adminHours;
        merged.directCosts.total += s.directCosts.total;
        merged.productivityLost.total += s.productivityLost.total;
        merged.systemicFriction.total += s.systemicFriction.total;
        merged.totalImpact += s.totalImpact;
        merged.infancyCost += s.infancyCost;
        if (s.productivityLost.monthlySalary > 0) {
            salarySum += s.productivityLost.monthlySalary;
            salaryCount += 1;
        }
    }
    merged.productivityLost.monthlySalary = salaryCount ? Math.round(salarySum / salaryCount) : 0;
    return merged;
}

/** Una fila por nivel; al guardar se replica en todas las áreas del tenant. */
function buildCidLevelRows(areaList, costsArray) {
    const areas = areaList || [];
    const areaIds = areas.map((a) => a.id);
    const defaultAreaId = areas[0]?.id ?? null;

    return CID_LEVEL_ORDER.map((level) => {
        const levelCosts = (costsArray || []).filter((c) => c.position_level === level);
        const ex =
            (defaultAreaId != null
                ? levelCosts.find((c) => c.area_id === defaultAreaId)
                : null) || levelCosts[0];
        const d = CID_DEFAULTS_BY_LEVEL[level];
        const persistedAreas = levelCosts.length;

        return {
            key: level,
            positionLevel: level,
            areaIds,
            defaultAreaId,
            recruitmentCost: ex?.recruitment_cost ?? d.recruitmentCost,
            severanceCost: ex?.severance_cost ?? d.severanceCost,
            ppeCost: ex?.ppe_cost ?? d.ppeCost,
            adminHoursCost: ex?.admin_hours_cost ?? d.adminHoursCost,
            learningCurveMonths: ex?.learning_curve_months ?? d.learningCurveMonths,
            gapPercent: Math.round(((ex?.gap_percentage ?? d.gap) || 0) * 100),
            masteryFactor: ex?.mastery_factor ?? d.masteryFactor,
            persisted: persistedAreas > 0,
            persistedAreas,
        };
    });
}

function masteryStateFromLevelRows(rows) {
    const out = {};
    for (const level of CID_LEVEL_ORDER) {
        const row = rows.find((r) => r.positionLevel === level);
        out[level] = row?.masteryFactor ?? CID_DEFAULTS_BY_LEVEL[level].masteryFactor;
    }
    return out;
}

function RiskPersonCard({ emp, selected, onToggle }) {
    return (
        <li>
            <button
                type="button"
                className={`calc-risk-card${selected ? ' calc-risk-card-on' : ''}`}
                onClick={() => onToggle(emp.id)}
                aria-pressed={selected}
            >
                <span className="calc-risk-check" aria-hidden>
                    {selected ? '✓' : ''}
                </span>
                <span className="calc-risk-card-body">
                    <span className="calc-risk-card-name">
                        {emp.full_name}{' '}
                        <span className="text-muted">({emp.employee_code})</span>
                    </span>
                    <span className="calc-risk-card-meta">
                        {emp.area_name}
                        {emp.position ? ` · ${emp.position}` : ''}
                        {Number(emp.monthly_salary) > 0 ? ` · ${formatCOP(emp.monthly_salary)}` : ''}
                    </span>
                    <span className="calc-risk-card-signals">{riskSignalsForWorker(emp).join(' · ')}</span>
                </span>
            </button>
        </li>
    );
}

export default function Calculator() {
    const { user } = useAuth();
    const caps = capabilitiesFromUser(user);
    const { tenantId: calcTenantId, isSuperAdmin, selectedTenantName } = useTenantScope();
    const [activeTab, setActiveTab] = useState('simulador');
    const [simulation, setSimulation] = useState(null);
    const [form, setForm] = useState({
        areaId: '',
        positionLevel: 'operativo',
        monthlySalary: '',
        quantity: '1',
        customDestabilization: '10',
        customGapPercentage: '50',
        biometricRisk: '1.0',
        scenarioType: 'rotacion',
        leaveDays: '15',
        ausentismoPct: '10',
        frictionMonths: '3',
        riskBand: '',
    });
    const [loading, setLoading] = useState(false);
    const [simulating, setSimulating] = useState(false);

    // Dashboard state
    const [dashboard, setDashboard] = useState(null);
    const [areas, setAreas] = useState([]);
    const [cidMatrix, setCidMatrix] = useState([]);
    const [cidCostsRaw, setCidCostsRaw] = useState([]);
    const [masteryByLevel, setMasteryByLevel] = useState(() => masteryStateFromLevelRows([]));
    const [cidLoading, setCidLoading] = useState(false);
    const [cidSavingKey, setCidSavingKey] = useState(null);
    const [cidDeletingKey, setCidDeletingKey] = useState(null);
    const [cidEditingKey, setCidEditingKey] = useState(null);
    const [masterySavingLevel, setMasterySavingLevel] = useState(null);
    const [riskAtWork, setRiskAtWork] = useState([]);
    const [recentDepartures, setRecentDepartures] = useState([]);
    const [riskLoading, setRiskLoading] = useState(false);
    const [salaryContext, setSalaryContext] = useState({
        byCargo: [],
        cohortAverage: null,
        cohortCount: 0,
        usedTechnicoWorkerFallback: false,
    });
    const [selectedCargo, setSelectedCargo] = useState('');
    const [selectedRiskIds, setSelectedRiskIds] = useState([]);
    const skipSalaryAutoRef = useRef(false);
    const riskSimAbortRef = useRef(0);
    const riskListKeyRef = useRef('');

    const applySalaryPayload = useCallback((data) => {
        const rows = (data.byCargo || []).map((r) => ({
            cargo: r.cargo,
            count: Number(r.count) || 0,
            avgMonthlySalary: Number(
                r.avgMonthlySalary ?? r.avgmonthlysalary ?? r.avg_monthly_salary ?? 0
            ),
        }));
        setSalaryContext({
            byCargo: rows,
            cohortAverage: data.cohortAverage ?? null,
            cohortCount: data.cohortCount ?? 0,
            usedTechnicoWorkerFallback: !!data.usedTechnicoWorkerFallback,
        });
        if (rows.length > 0 && rows[0].avgMonthlySalary > 0) {
            setSelectedCargo(rows[0].cargo);
            setForm((f) => ({ ...f, monthlySalary: String(rows[0].avgMonthlySalary) }));
        } else if (data.cohortAverage != null && Number(data.cohortAverage) > 0) {
            setSelectedCargo('');
            setForm((f) => ({ ...f, monthlySalary: String(data.cohortAverage) }));
        } else {
            setSelectedCargo(rows[0]?.cargo || '');
            setForm((f) => ({ ...f, monthlySalary: '' }));
        }
    }, []);

    const runSimulation = useCallback(
        async (payload) => {
            if (
                !payload?.areaId ||
                !payload?.positionLevel ||
                payload.monthlySalary == null ||
                payload.monthlySalary === '' ||
                Number(payload.monthlySalary) <= 0
            ) {
                setSimulation(null);
                return;
            }
            setSimulating(true);
            try {
                const res = await api.simulate(
                    {
                        areaId: Number(payload.areaId),
                        positionLevel: payload.positionLevel,
                        monthlySalary: Number(payload.monthlySalary),
                        quantity: Number(payload.quantity) || 1,
                        customDestabilization: payload.customDestabilization ?? '10',
                        customGapPercentage: payload.customGapPercentage ?? '50',
                        biometricRisk: Number(payload.biometricRisk) || 1,
                        scenarioType: payload.scenarioType || 'rotacion',
                        leaveDays: Number(payload.leaveDays) || 15,
                        ausentismoPct: Number(payload.ausentismoPct) || 10,
                        frictionMonths: Number(payload.frictionMonths) || 3,
                        riskBand: payload.riskBand || null,
                    },
                    calcTenantId
                );
                setSimulation(res);
            } catch (err) {
                setSimulation(null);
                console.error(err);
            } finally {
                setSimulating(false);
            }
        },
        [calcTenantId]
    );

    const syncFormFromWorkerPayload = useCallback((emp, payload) => {
        skipSalaryAutoRef.current = true;
        setSalaryContext(payload.salaryContext);
        setSelectedCargo(payload.cargo || '');
        setForm((f) => ({
            ...f,
            areaId: String(emp.area_id),
            positionLevel: payload.positionLevel,
            quantity: '1',
            monthlySalary: String(payload.monthlySalary),
        }));
    }, []);

    const runSimulationForWorkers = useCallback(
        async (workers, simForm) => {
            if (!workers?.length) {
                setSimulation(null);
                return;
            }
            const ctx = simForm || {
                customDestabilization: '10',
                customGapPercentage: '50',
                biometricRisk: '1.0',
                scenarioType: 'rotacion',
                leaveDays: '15',
                ausentismoPct: '10',
                frictionMonths: '3',
                riskBand: '',
            };
            const runId = ++riskSimAbortRef.current;
            setSimulating(true);
            try {
                const entries = [];
                const skipped = [];
                for (const emp of workers) {
                    const payload = await resolveWorkerSalaryPayload(emp, calcTenantId);
                    if (!payload) {
                        skipped.push(emp.full_name);
                        continue;
                    }
                    const res = await api.simulate(
                        {
                            areaId: payload.areaId,
                            positionLevel: payload.positionLevel,
                            monthlySalary: payload.monthlySalary,
                            quantity: 1,
                            customDestabilization: ctx.customDestabilization ?? '10',
                            customGapPercentage: ctx.customGapPercentage ?? '50',
                            biometricRisk: Number(ctx.biometricRisk) || 1,
                            scenarioType: ctx.scenarioType || 'rotacion',
                            leaveDays: Number(ctx.leaveDays) || 15,
                            ausentismoPct: Number(ctx.ausentismoPct) || 10,
                            frictionMonths: Number(ctx.frictionMonths) || 3,
                            riskBand: ctx.riskBand || null,
                        },
                        calcTenantId
                    );
                    entries.push({ emp, simulation: res, payload });
                }
                if (runId !== riskSimAbortRef.current) return;
                if (!entries.length) {
                    setSimulation(null);
                    if (skipped.length) console.warn('Sin salario para simular:', skipped.join(', '));
                    return;
                }
                if (entries.length === 1) {
                    syncFormFromWorkerPayload(entries[0].emp, entries[0].payload);
                    setSimulation(entries[0].simulation);
                } else {
                    setSimulation(mergeSimulationResults(entries));
                }
                setForm((f) => ({ ...f, quantity: String(workers.length) }));
            } catch (err) {
                if (runId === riskSimAbortRef.current) {
                    setSimulation(null);
                    console.error(err);
                }
            } finally {
                if (runId === riskSimAbortRef.current) setSimulating(false);
            }
        },
        [syncFormFromWorkerPayload, calcTenantId]
    );

    const toggleRiskSelection = useCallback((empId) => {
        setSelectedRiskIds((prev) => {
            const has = prev.includes(empId);
            return has ? prev.filter((id) => id !== empId) : [...prev, empId];
        });
    }, []);

    const selectAllRisk = useCallback(() => {
        setSelectedRiskIds(riskAtWork.map((e) => e.id));
    }, [riskAtWork]);

    const clearRiskSelection = useCallback(() => {
        setSelectedRiskIds([]);
        setSimulation(null);
    }, []);

    useEffect(() => {
        if (!isSuperAdmin) return;
        setRiskAtWork([]);
        setSelectedRiskIds([]);
        setSimulation(null);
        setRecentDepartures([]);
        riskListKeyRef.current = '';
    }, [calcTenantId, isSuperAdmin]);

    useEffect(() => {
        if (activeTab !== 'simulador' || !form.areaId) return undefined;
        if (isSuperAdmin && !calcTenantId) return undefined;
        if (skipSalaryAutoRef.current) {
            skipSalaryAutoRef.current = false;
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await api.getCalculatorSalaryAverages(
                    form.areaId,
                    form.positionLevel,
                    calcTenantId
                );
                if (cancelled) return;
                applySalaryPayload(data);
            } catch {
                if (!cancelled) {
                    setSalaryContext({
                        byCargo: [],
                        cohortAverage: null,
                        cohortCount: 0,
                        usedTechnicoWorkerFallback: false,
                    });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [form.areaId, form.positionLevel, activeTab, applySalaryPayload, calcTenantId, isSuperAdmin]);

    useEffect(() => {
        if (isSuperAdmin && !calcTenantId) {
            setAreas([]);
            return;
        }
        loadAreas();
        if (activeTab === 'dashboard') {
            loadDashboard();
        }
    }, [activeTab, calcTenantId, isSuperAdmin]);

    useEffect(() => {
        if (activeTab !== 'configuracion' || areas.length === 0) return undefined;
        if (isSuperAdmin && !calcTenantId) return undefined;
        let cancelled = false;
        (async () => {
            setCidLoading(true);
            try {
                const raw = await api.getCosts(calcTenantId);
                const list = Array.isArray(raw) ? raw : [];
                if (!cancelled) {
                    setCidCostsRaw(list);
                    const rows = buildCidLevelRows(areas, list);
                    setCidMatrix(rows);
                    setMasteryByLevel(masteryStateFromLevelRows(rows));
                }
            } catch (e) {
                console.warn('CID costs fetch failed', e);
                if (!cancelled) {
                    setCidCostsRaw([]);
                    const rows = buildCidLevelRows(areas, []);
                    setCidMatrix(rows);
                    setMasteryByLevel(masteryStateFromLevelRows(rows));
                }
            } finally {
                if (!cancelled) setCidLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activeTab, areas, calcTenantId, isSuperAdmin]);

    useEffect(() => {
        if (activeTab !== 'simulador') return undefined;
        if (isSuperAdmin && !calcTenantId) return undefined;
        let cancelled = false;
        setRiskLoading(true);
        (async () => {
            try {
                const [emplRes, histRes] = await Promise.all([
                    api.getEmployees(calcTenantId),
                    api.getRetirementHistory(calcTenantId),
                ]);
                if (cancelled) return;
                const emps = emplRes?.data || [];
                const at = emps.filter((e) => riskSignalsForWorker(e).length > 0);
                setRiskAtWork(at);
                const ev = histRes?.data || [];
                setRecentDepartures(Array.isArray(ev) ? ev.slice(0, 8) : []);
            } catch (e) {
                console.warn('Risk preview fetch failed', e);
                if (!cancelled) {
                    setRiskAtWork([]);
                    setRecentDepartures([]);
                }
            } finally {
                if (!cancelled) setRiskLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activeTab, calcTenantId, isSuperAdmin]);

    useEffect(() => {
        if (activeTab !== 'simulador' || riskLoading) return;
        if (!riskAtWork.length) {
            setSelectedRiskIds([]);
            riskListKeyRef.current = '';
            return;
        }
        const key = riskAtWork
            .map((e) => e.id)
            .sort((a, b) => a - b)
            .join(',');
        if (riskListKeyRef.current === key) return;
        riskListKeyRef.current = key;
        setSelectedRiskIds(riskAtWork.map((e) => e.id));
    }, [activeTab, riskLoading, riskAtWork]);

    useEffect(() => {
        if (activeTab !== 'simulador' || riskLoading) return undefined;
        const workers = riskAtWork.filter((e) => selectedRiskIds.includes(e.id));
        const simForm = {
            customDestabilization: form.customDestabilization,
            customGapPercentage: form.customGapPercentage,
            biometricRisk: form.biometricRisk,
            scenarioType: form.scenarioType,
            leaveDays: form.leaveDays,
            ausentismoPct: form.ausentismoPct,
            frictionMonths: form.frictionMonths,
            riskBand: form.riskBand,
        };
        // Con personas en riesgo: simular por perfil de cada una.
        if (workers.length) {
            runSimulationForWorkers(workers, simForm);
            return () => {
                riskSimAbortRef.current += 1;
            };
        }
        // Sin riesgo hoy: NO borrar resultados — calcular con el formulario
        // (área / nivel / salario). Antes setSimulation(null) dejaba la CID en blanco.
        if (
            form.areaId &&
            form.positionLevel &&
            form.monthlySalary &&
            Number(form.monthlySalary) > 0 &&
            form.quantity
        ) {
            runSimulation({
                areaId: form.areaId,
                positionLevel: form.positionLevel,
                monthlySalary: form.monthlySalary,
                quantity: form.quantity,
                ...simForm,
            });
            return undefined;
        }
        setSimulation(null);
        return undefined;
    }, [
        activeTab,
        riskLoading,
        riskAtWork,
        selectedRiskIds,
        form.areaId,
        form.positionLevel,
        form.monthlySalary,
        form.quantity,
        form.customDestabilization,
        form.customGapPercentage,
        form.biometricRisk,
        form.scenarioType,
        form.leaveDays,
        form.ausentismoPct,
        form.frictionMonths,
        form.riskBand,
        runSimulationForWorkers,
        runSimulation,
    ]);

    useEffect(() => {
        if (activeTab !== 'configuracion') {
            setCidEditingKey(null);
        }
    }, [activeTab]);

    async function loadAreas() {
        if (isSuperAdmin && !calcTenantId) {
            setAreas([]);
            setForm((f) => ({ ...f, areaId: '' }));
            return;
        }
        try {
            const areasRes = await api.getAdminAreas(calcTenantId);
            if (areasRes.success && areasRes.data?.length) {
                const list = areasRes.data;
                setAreas(list);
                setForm((f) => {
                    const stillValid = list.some((a) => String(a.id) === String(f.areaId));
                    return stillValid ? f : { ...f, areaId: String(list[0].id) };
                });
                return;
            }
        } catch (e) {
            console.warn('Areas fetch failed', e);
        }
        setAreas([]);
        setForm((f) => ({ ...f, areaId: '' }));
    }

    async function loadDashboard() {
        if (isSuperAdmin && !calcTenantId) return;
        setLoading(true);
        try {
            const res = await api.getFinancialDashboard(calcTenantId);
            setDashboard(res);
        } catch (err) {
            console.error('Error loading dashboard:', err);
        } finally {
            setLoading(false);
        }
    }

    function updateCidRow(key, patch) {
        setCidMatrix((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    }

    async function refreshCidMatrixFromServer() {
        const raw = await api.getCosts(calcTenantId);
        const list = Array.isArray(raw) ? raw : [];
        setCidCostsRaw(list);
        const rows = buildCidLevelRows(areas, list);
        setCidMatrix(rows);
        setMasteryByLevel(masteryStateFromLevelRows(rows));
    }

    function payloadFromCidRow(row, areaId, masteryOverride) {
        const ex = cidCostsRaw.find((c) => c.area_id === areaId && c.position_level === row.positionLevel);
        const d = CID_DEFAULTS_BY_LEVEL[row.positionLevel];
        const mastery =
            masteryOverride !== undefined
                ? masteryOverride
                : Number(masteryByLevel[row.positionLevel] ?? row.masteryFactor) || 0;
        return {
            areaId,
            positionLevel: row.positionLevel,
            recruitmentCost: Number(ex?.recruitment_cost ?? row.recruitmentCost ?? d.recruitmentCost) || 0,
            severanceCost: Number(ex?.severance_cost ?? row.severanceCost ?? d.severanceCost) || 0,
            ppeCost: Number(ex?.ppe_cost ?? row.ppeCost ?? d.ppeCost) || 0,
            adminHoursCost: Number(ex?.admin_hours_cost ?? row.adminHoursCost ?? d.adminHoursCost) || 0,
            learningCurveMonths: Number(ex?.learning_curve_months ?? row.learningCurveMonths ?? d.learningCurveMonths) || 0,
            gapPercentage:
                ex?.gap_percentage != null
                    ? Number(ex.gap_percentage)
                    : Math.min(100, Math.max(0, Number(row.gapPercent) || 0)) / 100,
            masteryFactor: mastery,
        };
    }

    async function saveCidRow(row) {
        if (!row.areaIds?.length) {
            alert('No hay áreas configuradas en la empresa.');
            return;
        }
        setCidSavingKey(row.key);
        try {
            const gapPct = Math.min(100, Math.max(0, Number(row.gapPercent) || 0)) / 100;
            const mastery = Number(masteryByLevel[row.positionLevel] ?? row.masteryFactor) || 0;
            for (const areaId of row.areaIds) {
                await api.updateCosts(
                    {
                        areaId,
                        positionLevel: row.positionLevel,
                        recruitmentCost: Number(row.recruitmentCost) || 0,
                        severanceCost: Number(row.severanceCost) || 0,
                        ppeCost: Number(row.ppeCost) || 0,
                        adminHoursCost: Number(row.adminHoursCost) || 0,
                        learningCurveMonths: Number(row.learningCurveMonths) || 0,
                        gapPercentage: gapPct,
                        masteryFactor: mastery,
                    },
                    calcTenantId
                );
            }
            await refreshCidMatrixFromServer();
            setCidEditingKey(null);
        } catch (err) {
            alert(err.message || 'No se pudo guardar el maestro CID');
        } finally {
            setCidSavingKey(null);
        }
    }

    async function saveMasteryLevel(level) {
        const row = cidMatrix.find((r) => r.positionLevel === level);
        if (!row?.areaIds?.length) {
            alert('No hay áreas configuradas en la empresa.');
            return;
        }
        const masteryFactor = Number(masteryByLevel[level]) || 0;
        setMasterySavingLevel(level);
        try {
            for (const areaId of row.areaIds) {
                await api.updateCosts(payloadFromCidRow(row, areaId, masteryFactor), calcTenantId);
            }
            await refreshCidMatrixFromServer();
        } catch (err) {
            alert(err.message || 'No se pudo guardar el factor de maestría');
        } finally {
            setMasterySavingLevel(null);
        }
    }

    async function deleteCidRow(row) {
        if (!row.persisted) return;
        if (
            !confirm(
                `¿Eliminar los parámetros CID de nivel «${CID_LEVEL_LABEL[row.positionLevel]}» en todas las áreas? El simulador usará valores por defecto hasta volver a guardar.`
            )
        )
            return;
        setCidDeletingKey(row.key);
        try {
            for (const areaId of row.areaIds) {
                await api.deleteCalculatorCosts(areaId, row.positionLevel, calcTenantId);
            }
            if (cidEditingKey === row.key) setCidEditingKey(null);
            await refreshCidMatrixFromServer();
        } catch (err) {
            alert(err.message || 'No se pudo eliminar');
        } finally {
            setCidDeletingKey(null);
        }
    }

    async function cancelCidEdit() {
        setCidEditingKey(null);
        try {
            await refreshCidMatrixFromServer();
        } catch (e) {
            console.warn(e);
        }
    }

    async function handleSimulate() {
        const workers = riskAtWork.filter((e) => selectedRiskIds.includes(e.id));
        if (workers.length > 0) {
            await runSimulationForWorkers(workers, {
                customDestabilization: form.customDestabilization,
                customGapPercentage: form.customGapPercentage,
                biometricRisk: form.biometricRisk,
                scenarioType: form.scenarioType,
                leaveDays: form.leaveDays,
                ausentismoPct: form.ausentismoPct,
                frictionMonths: form.frictionMonths,
                riskBand: form.riskBand,
            });
            return;
        }
        if (!form.areaId || !form.positionLevel || !form.monthlySalary || !form.quantity) {
            return alert('Completa área, nivel y salario para calcular.');
        }
        await runSimulation({
            areaId: form.areaId,
            positionLevel: form.positionLevel,
            monthlySalary: form.monthlySalary,
            quantity: form.quantity,
            customDestabilization: form.customDestabilization,
            customGapPercentage: form.customGapPercentage,
            biometricRisk: form.biometricRisk,
            scenarioType: form.scenarioType,
            leaveDays: form.leaveDays,
            ausentismoPct: form.ausentismoPct,
            frictionMonths: form.frictionMonths,
            riskBand: form.riskBand,
        });
    }

    const fmt = (n) => formatCOPAllowZero(n);

    const selectedRiskWorkers = useMemo(
        () => riskAtWork.filter((e) => selectedRiskIds.includes(e.id)),
        [riskAtWork, selectedRiskIds]
    );

    const allRiskSelected =
        riskAtWork.length > 0 && selectedRiskIds.length === riskAtWork.length;

    const impactChartData = useMemo(() => {
        if (!simulation) return [];
        const st = simulation.scenarioType || form.scenarioType || 'rotacion';
        const rows =
            st === 'incapacidad'
                ? [
                      { component: 'Overhead SST', monto: simulation.directCosts?.total ?? 0 },
                      { component: 'Costo reemplazo', monto: simulation.productivityLost?.total ?? 0 },
                      { component: 'Prima de riesgo', monto: simulation.systemicFriction?.total ?? 0 },
                  ]
                : st === 'accidente_ausentismo'
                  ? [
                        { component: 'Costo ausentismo', monto: simulation.productivityLost?.total ?? 0 },
                        {
                            component: 'Fricción accidentalidad',
                            monto: simulation.systemicFriction?.total ?? 0,
                        },
                        { component: 'Costos directos', monto: simulation.directCosts?.total ?? 0 },
                    ]
                  : [
                        { component: 'Costos directos', monto: simulation.directCosts?.total ?? 0 },
                        { component: 'Prod. perdida', monto: simulation.productivityLost?.total ?? 0 },
                        { component: 'Fricción sistémica', monto: simulation.systemicFriction?.total ?? 0 },
                    ];
        return rows.filter((r) => r.monto > 0);
    }, [simulation, form.scenarioType]);

    const impactChartPctData = useMemo(() => {
        if (!simulation?.totalImpact) return [];
        return impactChartData.map((r) => ({
            ...r,
            pct: Math.round((r.monto / simulation.totalImpact) * 1000) / 10,
        }));
    }, [impactChartData, simulation]);

    const scenarioKind = simulation?.scenarioType || form.scenarioType || 'rotacion';

    useEffect(() => {
        if (!simulation || simulating) return;
        const el = document.getElementById('cid-impact-results');
        if (!el) return;
        const t = window.setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 80);
        return () => window.clearTimeout(t);
    }, [simulation, simulating]);

    const rotacionAnualChartData = useMemo(
        () => (dashboard ? construirSerieRotacionAnual(dashboard.monthlyTrend) : []),
        [dashboard]
    );

    return (
        <div className="container container-wide animate-in">
            <header className="calc-header mb-xl">
                <div>
                    <h2>💰 Calculadora CID</h2>
                    <p className="text-muted">
                        Costo de Impacto por Desconexión — visibilidad económica preventiva de rotación y pérdida de
                        continuidad operativa
                        {isSuperAdmin && selectedTenantName ? (
                            <>
                                {' '}
                                · <strong>{selectedTenantName}</strong>
                            </>
                        ) : null}
                    </p>
                </div>
                <div className="tab-navigation">
                    <button
                        className={`tab-btn ${activeTab === 'simulador' ? 'active' : ''}`}
                        onClick={() => setActiveTab('simulador')}
                    >
                        🧮 Simular Escenario
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                        onClick={() => setActiveTab('dashboard')}
                    >
                        📊 Dashboard Financiero
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'configuracion' ? 'active' : ''}`}
                        onClick={() => setActiveTab('configuracion')}
                    >
                        ⚙️ Configuración CID
                    </button>
                </div>
            </header>

            {loading && <div className="loading-screen" style={{ position: 'relative', height: '100px' }}><div className="spinner" /></div>}

            {!loading && (activeTab === 'simulador' ? (
                <div className="stagger">
                    <div className="glass-card shadow-sm p-md calc-risk-panel" style={{ borderLeft: '4px solid #dc2626' }}>
                        <div className="calc-risk-panel-head">
                            <h3>
                                {form.scenarioType === 'incapacidad'
                                    ? 'Quién podría ausentarse'
                                    : form.scenarioType === 'accidente_ausentismo'
                                      ? 'Quién concentra riesgo de accidentalidad'
                                      : 'Quién podría irse'}
                            </h3>
                            {riskAtWork.length > 0 && !riskLoading ? (
                                <div className="calc-risk-toolbar">
                                    <button
                                        type="button"
                                        className={`calc-risk-toolbar-btn${allRiskSelected ? ' calc-risk-toolbar-btn-on' : ''}`}
                                        onClick={selectAllRisk}
                                    >
                                        Todos ({riskAtWork.length})
                                    </button>
                                    <button
                                        type="button"
                                        className="calc-risk-toolbar-btn"
                                        onClick={clearRiskSelection}
                                        disabled={selectedRiskIds.length === 0}
                                    >
                                        Ninguno
                                    </button>
                                </div>
                            ) : null}
                        </div>
                        <p className="calc-discrete-hint mb-md">
                            Señales del equipo (check-in y alertas). Por defecto se calcula con{' '}
                            <strong>todos</strong> en riesgo; marca o desmarca personas. El gráfico y el
                            detalle del CID se mantienen debajo al recalcular. Escenarios (rotación,
                            accidentalidad, ausentismo) son independientes.
                        </p>
                        {riskLoading ? (
                            <div className="flex items-center gap-sm text-muted text-sm">
                                <div className="spinner" style={{ width: 22, height: 22 }} />
                                Cargando…
                            </div>
                        ) : riskAtWork.length === 0 ? (
                            <p className="calc-discrete-hint">Nadie cumple hoy criterio de riesgo elevado.</p>
                        ) : (
                            <>
                                <p className="calc-risk-selection-summary">
                                    {selectedRiskIds.length === 0 ? (
                                        'Ninguna persona seleccionada'
                                    ) : (
                                        <>
                                            <strong>{selectedRiskIds.length}</strong> de {riskAtWork.length}{' '}
                                            {selectedRiskIds.length === 1 ? 'persona' : 'personas'} en el cálculo
                                        </>
                                    )}
                                </p>
                                <ul className="calc-risk-list">
                                    {riskAtWork.map((emp) => (
                                        <RiskPersonCard
                                            key={emp.id}
                                            emp={emp}
                                            selected={selectedRiskIds.includes(emp.id)}
                                            onToggle={toggleRiskSelection}
                                        />
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>

                    <div className="glass-card shadow-sm p-md">
                        <div className="calc-form-head">
                            <h3>
                                {form.scenarioType === 'incapacidad'
                                    ? 'Simular ausentismo / incapacidad'
                                    : form.scenarioType === 'accidente_ausentismo'
                                      ? 'Simular accidentalidad'
                                      : 'Simular renuncia'}
                            </h3>
                            <p className="calc-discrete-hint">
                                COP · datos desde Equipo (editables)
                                {selectedRiskWorkers.length > 0 ? (
                                    <>
                                        {' '}
                                        · selección:{' '}
                                        <strong>
                                            {selectedRiskWorkers.length === 1
                                                ? selectedRiskWorkers[0].full_name
                                                : `${selectedRiskWorkers.length} personas`}
                                        </strong>
                                    </>
                                ) : null}
                            </p>
                        </div>

                        <div className="calc-scenario-tabs" role="tablist" aria-label="Tipo de escenario CID">
                            {[
                                { id: 'rotacion', label: 'Rotación' },
                                ...(caps.showCidScenariosExtended
                                    ? [
                                          { id: 'accidente_ausentismo', label: 'Accidentalidad' },
                                          { id: 'incapacidad', label: 'Ausentismo' },
                                      ]
                                    : []),
                            ].map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={form.scenarioType === s.id}
                                    className={`calc-scenario-tab${form.scenarioType === s.id ? ' on' : ''}`}
                                    onClick={() => setForm((f) => ({ ...f, scenarioType: s.id }))}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>

                        <div className="calc-form-horizontal" style={{ flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
                            <div className="form-group">
                                <label className="calc-label">Área</label>
                                <select
                                    className="input w-100"
                                    value={form.areaId}
                                    disabled={!areas.length}
                                    onChange={(e) => {
                                        setSelectedRiskIds([]);
                                        setForm((f) => ({ ...f, areaId: e.target.value }));
                                    }}
                                >
                                    {!areas.length ? (
                                        <option value="">Cargando áreas o sin permisos…</option>
                                    ) : (
                                        areas.map((a) => (
                                            <option key={a.id} value={String(a.id)}>
                                                {a.name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="calc-label">Nivel</label>
                                <select
                                    className="input w-100"
                                    value={form.positionLevel}
                                    onChange={(e) => {
                                        setSelectedRiskIds([]);
                                        setForm((f) => ({ ...f, positionLevel: e.target.value }));
                                    }}
                                >
                                    <option value="operativo">Operativo base</option>
                                    <option value="tecnico">Técnico especializado</option>
                                    <option value="supervision">Supervisión / Liderazgo</option>
                                </select>
                            </div>
                            <div className="form-group" style={{ minWidth: 'min(100%, 280px)', flex: '1 1 220px' }}>
                                <label className="calc-label" title="Promedio por puesto en Equipo para el área y nivel">
                                    Cargo
                                </label>
                                <select
                                    className="input w-100"
                                    value={
                                        salaryContext.byCargo.length === 0
                                            ? ''
                                            : selectedCargo &&
                                                salaryContext.byCargo.some((r) => r.cargo === selectedCargo)
                                              ? selectedCargo
                                              : salaryContext.byCargo[0].cargo
                                    }
                                    onChange={(e) => {
                                        setSelectedRiskIds([]);
                                        const val = e.target.value;
                                        setSelectedCargo(val);
                                        const row = salaryContext.byCargo.find((r) => r.cargo === val);
                                        const sal = Number(
                                            row?.avgMonthlySalary ?? row?.avgmonthlysalary ?? 0
                                        );
                                        if (row && sal > 0) {
                                            setForm((f) => ({ ...f, monthlySalary: String(sal) }));
                                        }
                                    }}
                                    disabled={!salaryContext.byCargo.length}
                                >
                                    {!salaryContext.byCargo.length ? (
                                        <option value="">— Sin datos de cargo en esta área/nivel —</option>
                                    ) : (
                                        salaryContext.byCargo.map((r) => (
                                            <option key={r.cargo} value={r.cargo}>
                                                {r.cargo} — {formatCOP(r.avgMonthlySalary)} (n={r.count})
                                            </option>
                                        ))
                                    )}
                                </select>
                                {salaryContext.usedTechnicoWorkerFallback ? (
                                    <span className="calc-field-note">Promedio del área (sin match técnico por nombre).</span>
                                ) : null}
                            </div>
                            <div className="form-group" style={{ minWidth: '140px' }}>
                                <label className="calc-label" title="Sueldo en Equipo o promedio del cargo">
                                    Salario (COP)
                                </label>
                                <input
                                    className="input w-100"
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="Ej: 2000000"
                                    value={form.monthlySalary}
                                    onChange={(e) => setForm((f) => ({ ...f, monthlySalary: e.target.value }))}
                                />
                                {salaryContext.cohortAverage != null && salaryContext.cohortCount > 0 ? (
                                    <button
                                        type="button"
                                        className="calc-link-hint"
                                        onClick={() => {
                                            setSelectedRiskIds([]);
                                            setForm((f) => ({ ...f, monthlySalary: String(salaryContext.cohortAverage) }));
                                        }}
                                    >
                                        Promedio nivel {formatCOP(salaryContext.cohortAverage)} (n={salaryContext.cohortCount})
                                    </button>
                                ) : null}
                            </div>
                            <div className="form-group" style={{ maxWidth: '80px' }}>
                                <label className="calc-label" title={selectedRiskIds.length > 0 ? 'Sincronizado con la selección de riesgo' : undefined}>
                                    Cant.
                                </label>
                                <input
                                    className="input w-100"
                                    type="number"
                                    min="1"
                                    value={selectedRiskIds.length > 0 ? String(selectedRiskIds.length) : form.quantity}
                                    readOnly={selectedRiskIds.length > 0}
                                    onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                                />
                            </div>

                            <div style={{ width: '100%' }}></div> {/* Force wrap for advanced settings */}

                            {form.scenarioType === 'rotacion' && (
                                <>
                                    <div className="form-group" style={{ maxWidth: '160px' }}>
                                        <label className="calc-label" title="Brecha de productividad 0–100">% Brecha</label>
                                        <input
                                            className="input w-100"
                                            type="number"
                                            min="0"
                                            max="100"
                                            placeholder="50"
                                            value={form.customGapPercentage}
                                            onChange={(e) => setForm((f) => ({ ...f, customGapPercentage: e.target.value }))}
                                        />
                                    </div>
                                    <div className="form-group" style={{ maxWidth: '200px' }}>
                                        <label className="calc-label" title="Por cada persona adicional">% Desestab.</label>
                                        <input
                                            className="input w-100"
                                            type="number"
                                            min="0"
                                            max="100"
                                            placeholder="10"
                                            value={form.customDestabilization}
                                            onChange={(e) => setForm((f) => ({ ...f, customDestabilization: e.target.value }))}
                                        />
                                    </div>
                                </>
                            )}

                            {form.scenarioType === 'incapacidad' && (
                                <>
                                    <div className="form-group" style={{ maxWidth: '140px' }}>
                                        <label className="calc-label">Días</label>
                                        <select
                                            className="input w-100"
                                            value={form.leaveDays}
                                            onChange={(e) => setForm((f) => ({ ...f, leaveDays: e.target.value }))}
                                        >
                                            <option value="3">3</option>
                                            <option value="7">7</option>
                                            <option value="15">15</option>
                                            <option value="30">30</option>
                                            <option value="90">90</option>
                                        </select>
                                    </div>
                                    <div className="form-group" style={{ maxWidth: '160px' }}>
                                        <label className="calc-label">Banda riesgo</label>
                                        <select
                                            className="input w-100"
                                            value={form.riskBand}
                                            onChange={(e) => setForm((f) => ({ ...f, riskBand: e.target.value }))}
                                        >
                                            <option value="">Desde contexto</option>
                                            <option value="bajo">Bajo</option>
                                            <option value="medio">Medio</option>
                                            <option value="alto">Alto</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {form.scenarioType === 'accidente_ausentismo' && (
                                <>
                                    <div className="form-group" style={{ maxWidth: '140px' }}>
                                        <label className="calc-label">% Ausentismo</label>
                                        <input
                                            className="input w-100"
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={form.ausentismoPct}
                                            onChange={(e) => setForm((f) => ({ ...f, ausentismoPct: e.target.value }))}
                                        />
                                    </div>
                                    <div className="form-group" style={{ maxWidth: '140px' }}>
                                        <label className="calc-label">Meses fricción</label>
                                        <input
                                            className="input w-100"
                                            type="number"
                                            min="1"
                                            max="12"
                                            value={form.frictionMonths}
                                            onChange={(e) => setForm((f) => ({ ...f, frictionMonths: e.target.value }))}
                                        />
                                    </div>
                                    <div className="form-group" style={{ maxWidth: '160px' }}>
                                        <label className="calc-label">Banda riesgo</label>
                                        <select
                                            className="input w-100"
                                            value={form.riskBand}
                                            onChange={(e) => setForm((f) => ({ ...f, riskBand: e.target.value }))}
                                        >
                                            <option value="">Desde contexto</option>
                                            <option value="bajo">Bajo</option>
                                            <option value="medio">Medio</option>
                                            <option value="alto">Alto</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className="form-group" style={{ maxWidth: '200px' }}>
                                <label className="calc-label" title="Factor opcional de contexto">Contexto</label>
                                <div className="flex items-center gap-sm">
                                    <input
                                        type="range"
                                        min="1.0"
                                        max="2.0"
                                        step="0.1"
                                        value={form.biometricRisk}
                                        onChange={(e) => setForm((f) => ({ ...f, biometricRisk: e.target.value }))}
                                        style={{ accentColor: 'var(--color-primary)' }}
                                    />
                                    <span className="text-sm font-bold">{form.biometricRisk}x</span>
                                </div>
                            </div>

                            <button
                                type="button"
                                className="btn btn-primary h-form-btn"
                                disabled={!form.areaId || !areas.length || simulating}
                                onClick={handleSimulate}
                            >
                                {simulating ? 'Calculando…' : 'Recalcular impacto'}
                            </button>
                        </div>
                    </div>

                    {(simulating || simulation) ? (
                        <div
                            id="cid-impact-results"
                            className="glass-card shadow-sm p-md mt-lg calc-impact-visual"
                        >
                            <h3 className="calc-impact-visual-title">
                                {scenarioKind === 'incapacidad'
                                    ? simulation?.quantity > 1
                                        ? 'Proyección de ausentismo / incapacidad (grupo)'
                                        : 'Proyección de ausentismo / incapacidad'
                                    : scenarioKind === 'accidente_ausentismo'
                                      ? simulation?.quantity > 1
                                          ? 'Proyección de accidentalidad (grupo)'
                                          : 'Proyección de accidentalidad'
                                      : simulation?.quantity > 1
                                        ? 'Proyección si salen del equipo'
                                        : 'Proyección si sale del equipo'}
                            </h3>
                            {selectedRiskWorkers.length > 0 && (
                                <p className="calc-discrete-hint mb-md">
                                    {selectedRiskWorkers.length === 1 ? (
                                        <>
                                            {selectedRiskWorkers[0].full_name} · {selectedRiskWorkers[0].area_name}
                                            {selectedRiskWorkers[0].position
                                                ? ` · ${selectedRiskWorkers[0].position}`
                                                : ''}
                                        </>
                                    ) : (
                                        <>
                                            {selectedRiskWorkers.map((e) => e.full_name).join(' · ')}
                                            {simulation?.aggregated ? (
                                                <span className="calc-discrete-hint">
                                                    {' '}
                                                    — suma individual por área, nivel y salario de cada persona
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </p>
                            )}
                            {simulation?.aggregated && simulation.selectedWorkers?.length > 1 ? (
                                <ul className="calc-per-person-impact">
                                    {simulation.selectedWorkers.map((w) => (
                                        <li key={w.id}>
                                            <span>{w.name}</span>
                                            <span className="text-muted">{w.areaName}</span>
                                            <strong>{fmt(w.impact)}</strong>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                            {simulating ? (
                                <div className="calc-chart-loading">
                                    <div className="spinner" />
                                    <span>Calculando impacto CID…</span>
                                </div>
                            ) : simulation ? (
                                <div className="calc-impact-layout">
                                    <div className="calc-impact-chart-wrap">
                                        <p className="calc-chart-caption">Desglose del costo (COP)</p>
                                        <div className="calc-chart-box">
                                            <ResponsiveContainer width="100%" height={260}>
                                                <BarChart
                                                    data={impactChartData}
                                                    layout="vertical"
                                                    margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                    <XAxis
                                                        type="number"
                                                        tickFormatter={(v) =>
                                                            v >= 1_000_000
                                                                ? `${Math.round(v / 1_000_000)}M`
                                                                : `${Math.round(v / 1000)}k`
                                                        }
                                                    />
                                                    <YAxis type="category" dataKey="component" width={118} tick={{ fontSize: 11 }} />
                                                    <Tooltip
                                                        formatter={(value) => formatCOPAllowZero(value)}
                                                        labelFormatter={(label) => label}
                                                    />
                                                    <Bar dataKey="monto" radius={[0, 6, 6, 0]} maxBarSize={36}>
                                                        {impactChartData.map((_, i) => (
                                                            <Cell key={i} fill={IMPACT_CHART_COLORS[i % IMPACT_CHART_COLORS.length]} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <ul className="calc-chart-legend">
                                            {impactChartPctData.map((row, i) => (
                                                <li key={row.component}>
                                                    <span
                                                        className="calc-chart-dot"
                                                        style={{ background: IMPACT_CHART_COLORS[i % IMPACT_CHART_COLORS.length] }}
                                                    />
                                                    {row.component}: <strong>{row.pct}%</strong> ({fmt(row.monto)})
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="calc-impact-total-wrap">
                                        <div className="calc-impact-total-card">
                                            <span className="calc-impact-total-label">Impacto total proyectado</span>
                                            <span className="calc-impact-total-value">{fmt(simulation.totalImpact)}</span>
                                            <span className="calc-impact-total-sub">
                                                {simulation.quantity}{' '}
                                                {simulation.quantity === 1 ? 'persona' : 'personas'}
                                                {simulation.aggregated
                                                    ? ' · cálculo agregado por perfil'
                                                    : ` · ${CID_LEVEL_LABEL[form.positionLevel] || form.positionLevel}`}
                                            </span>
                                            {simulation.supervisorName && (
                                                <span className="calc-impact-total-sub">
                                                    {simulation.quantity > 1 ? 'Supervisores: ' : 'Supervisor del área: '}
                                                    {simulation.supervisorName}
                                                </span>
                                            )}
                                        </div>
                                        {simulation.costsEstimated && (
                                            <p className="calc-field-note" style={{ marginTop: '0.75rem' }}>
                                                Costos maestros no configurados: parte del desglose usa valores en cero.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <div
                            id="cid-impact-results"
                            className="glass-card shadow-sm p-md mt-lg calc-impact-visual"
                        >
                            <h3 className="calc-impact-visual-title">Proyección de impacto</h3>
                            <p className="calc-discrete-hint mb-0">
                                {form.monthlySalary && Number(form.monthlySalary) > 0
                                    ? 'Calculando o listo para recalcular. Si no ves el gráfico, pulsa «Recalcular impacto».'
                                    : 'Indica un salario (COP) en el formulario — se rellena solo si el cargo tiene salario en Equipo — y pulsa «Recalcular impacto». El desglose y el detalle aparecen aquí.'}
                            </p>
                        </div>
                    )}

                    {recentDepartures.length > 0 && (
                        <div className="glass-card shadow-sm p-md mt-lg calc-historial-compact">
                            <h4 className="calc-subsection-title">Bajas registradas (referencia)</h4>
                            <ul className="cid-historial-list">
                                {recentDepartures.map((ev) => (
                                    <li key={ev.id} className="cid-historial-item">
                                        <span>
                                            <strong>{ev.employee_name}</strong> · {ev.area_name} · {ev.event_date}
                                        </span>
                                        <span className="text-muted">
                                            {fmt(ev.total_impact)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {simulation && !simulating && (
                        <div className="calc-results-wrapper stagger mt-lg" aria-labelledby="cid-detalle-titulo">
                            <h3
                                id="cid-detalle-titulo"
                                className="calc-subsection-title"
                                style={{ fontSize: '1rem', marginBottom: '1rem' }}
                            >
                                Detalle del cálculo
                            </h3>
                            <div className="grid-3 mt-lg">
                                <div className="glass-card stat-card">
                                    <h4>
                                        {scenarioKind === 'incapacidad'
                                            ? '📋 Overhead SST'
                                            : scenarioKind === 'accidente_ausentismo'
                                              ? '📋 Costos directos'
                                              : '📋 Costos Directos'}
                                    </h4>
                                    <p className="stat-value text-red">{fmt(simulation.directCosts.total)}</p>
                                    {scenarioKind === 'rotacion' ? (
                                        <ul className="stat-breakdown text-xs mt-sm">
                                            <li>Reclutamiento: {fmt(simulation.directCosts.recruitment)}</li>
                                            <li>Indemnización: {fmt(simulation.directCosts.severance)}</li>
                                            <li>EPP y Herramientas: {fmt(simulation.directCosts.ppe)}</li>
                                            <li>Procesos Admin: {fmt(simulation.directCosts.adminHours)}</li>
                                        </ul>
                                    ) : (
                                        <p className="text-xs mt-sm opacity-90">
                                            {scenarioKind === 'incapacidad'
                                                ? 'Sobrecosto administrativo / SST del escenario de ausencia.'
                                                : 'En accidentalidad el foco está en ausentismo + fricción latente.'}
                                        </p>
                                    )}
                                </div>

                                <div className="glass-card stat-card">
                                    <h4>
                                        {scenarioKind === 'incapacidad'
                                            ? '📉 Costo de reemplazo'
                                            : scenarioKind === 'accidente_ausentismo'
                                              ? '📉 Costo por ausentismo'
                                              : '📉 Prod. Perdida'}
                                    </h4>
                                    <p className="stat-value text-orange">{fmt(simulation.productivityLost.total)}</p>
                                    <p className="text-xs mt-sm opacity-90">
                                        {simulation.aggregated
                                            ? 'Salario mensual promedio'
                                            : 'Salario mensual aplicado al modelo'}
                                        : <strong>{fmt(simulation.productivityLost.monthlySalary)}</strong> (COP).
                                    </p>
                                    <p className="text-xs mt-sm opacity-70">
                                        {scenarioKind === 'incapacidad'
                                            ? `Días / curva: ${simulation.productivityLost.learningCurveMonths} meses equivalentes.`
                                            : scenarioKind === 'accidente_ausentismo'
                                              ? `Horizonte: ${simulation.productivityLost.learningCurveMonths} meses · % ausentismo en modelo.`
                                              : `Curva de aprendizaje: ${simulation.productivityLost.learningCurveMonths} meses por persona.`}
                                    </p>
                                </div>

                                <div
                                    className="glass-card stat-card"
                                    style={{
                                        border:
                                            simulation.systemicFriction &&
                                            simulation.systemicFriction.status ===
                                                'Señales elevadas (simulación)'
                                                ? '2px solid #ef4444'
                                                : 'none',
                                    }}
                                >
                                    <h4>
                                        {scenarioKind === 'accidente_ausentismo'
                                            ? '🔥 Fricción por accidentalidad'
                                            : scenarioKind === 'incapacidad'
                                              ? '🔥 Factor de riesgo'
                                              : '🔥 Fricción Sistémica'}
                                    </h4>
                                    <p className="stat-value text-orange">
                                        {fmt(simulation.systemicFriction.total)}
                                    </p>
                                    <div className="text-xs mt-sm">
                                        <p className="opacity-70">
                                            Factor Maestría: {simulation.systemicFriction.baseMasteryFactor} ×{' '}
                                            {simulation.systemicFriction.biometricRiskMultiplier} (contexto)
                                        </p>
                                        <p
                                            className="mt-xs"
                                            style={{
                                                color:
                                                    simulation.systemicFriction.status ===
                                                    'Señales elevadas (simulación)'
                                                        ? '#ef4444'
                                                        : 'var(--color-text-secondary)',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {simulation.systemicFriction.status}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {scenarioKind === 'rotacion' && (
                                <div className="glass-card infancy-warning mt-lg">
                                    <div className="flex items-center gap-md">
                                        <div className="infancy-icon">⏳</div>
                                        <div>
                                            <h4>Infancia Operativa Perpetua</h4>
                                            <p className="text-sm opacity-80">
                                                Esta rotación le cuesta anualmente{' '}
                                                <strong>{fmt(simulation.infancyCost)}</strong> extra debido a
                                                puestos que nunca alcanzan la maestría total.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {simulation.disclaimer ? (
                                <p className="calc-discrete-hint mt-md">{simulation.disclaimer}</p>
                            ) : null}
                        </div>
                    )}
                </div>
            ) : activeTab === 'dashboard' ? (
                dashboard ? (
                    <div className="dashboard-results stagger">
                        <div className="glass-card mb-lg calc-dash-hero" style={{ borderLeft: '4px solid var(--color-alert-red)' }}>
                            <h3>Costo acumulado de rotación del año</h3>
                            <p className="calc-dash-hero-value">{fmt(dashboard.yearly?.total_cost || 0)}</p>
                            <p className="text-muted text-sm">
                                Costo total de {dashboard.yearly?.total_events || 0}{' '}
                                {dashboard.yearly?.total_events === 1 ? 'baja' : 'bajas'} registradas desde enero hasta hoy (
                                {new Date().getFullYear()}).
                            </p>

                            {dashboard.yearly?.total_cost > 0 && (
                                <div className="calc-dash-ahorro mt-md">
                                    <h4>Proyección de ahorro</h4>
                                    <p>
                                        Si se reduce la rotación un <strong>20%</strong> en los próximos 12 meses, el ahorro
                                        estimado sería:
                                    </p>
                                    <p className="calc-dash-ahorro-valor">{fmt((dashboard.yearly?.total_cost || 0) * 0.2)}</p>
                                </div>
                            )}
                        </div>

                        <div className="glass-card mb-lg calc-dash-chart-card">
                            <h3>Rotación mes a mes y acumulado del año</h3>
                            <p className="calc-discrete-hint mb-md">
                                Barras: costo del mes · Línea: suma acumulada desde enero (COP).
                            </p>
                            {rotacionAnualChartData.length === 0 ? (
                                <p className="text-muted">Aún no hay bajas registradas este año.</p>
                            ) : (
                                <div className="calc-dash-chart-box">
                                    <ResponsiveContainer width="100%" height={320}>
                                        <ComposedChart
                                            data={rotacionAnualChartData}
                                            margin={{ top: 12, right: 16, left: 8, bottom: 8 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                                            <YAxis
                                                yAxisId="left"
                                                tickFormatter={(v) =>
                                                    v >= 1_000_000
                                                        ? `${Math.round(v / 1_000_000)}M`
                                                        : `${Math.round(v / 1000)}k`
                                                }
                                                tick={{ fontSize: 11 }}
                                            />
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                tickFormatter={(v) =>
                                                    v >= 1_000_000
                                                        ? `${Math.round(v / 1_000_000)}M`
                                                        : `${Math.round(v / 1000)}k`
                                                }
                                                tick={{ fontSize: 11 }}
                                            />
                                            <Tooltip
                                                formatter={(value, name) => [
                                                    formatCOPAllowZero(value),
                                                    name === 'costoMes'
                                                        ? 'Costo del mes'
                                                        : name === 'acumulado'
                                                          ? 'Acumulado del año'
                                                          : name,
                                                ]}
                                                labelFormatter={(label) => `Mes: ${label}`}
                                            />
                                            <Legend
                                                formatter={(value) =>
                                                    value === 'costoMes'
                                                        ? 'Costo del mes'
                                                        : value === 'acumulado'
                                                          ? 'Acumulado del año'
                                                          : value
                                                }
                                            />
                                            <Bar
                                                yAxisId="left"
                                                dataKey="costoMes"
                                                name="costoMes"
                                                fill="#dc2626"
                                                radius={[4, 4, 0, 0]}
                                                maxBarSize={40}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="acumulado"
                                                name="acumulado"
                                                stroke="#312e81"
                                                strokeWidth={2.5}
                                                dot={{ r: 4, fill: '#312e81' }}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {rotacionAnualChartData.length > 0 ? (
                                <ul className="calc-dash-mes-resumen">
                                    {rotacionAnualChartData.map((row) => (
                                        <li key={row.mes}>
                                            <span>{row.mes}</span>
                                            <span>
                                                {row.bajas} {row.bajas === 1 ? 'baja' : 'bajas'} · mes {fmt(row.costoMes)} · acum.{' '}
                                                {fmt(row.acumulado)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>

                        <div className="grid-2">
                            <div className="glass-card">
                                <h3>Áreas con mayor impacto</h3>
                                <div className="mt-md calc-dash-ranking">
                                    {(!dashboard.byArea || dashboard.byArea.length === 0) && (
                                        <p className="text-muted">No hay datos suficientes.</p>
                                    )}
                                    {dashboard.byArea?.map((area, idx) => (
                                        <div
                                            key={area.area_name}
                                            className={`calc-dash-ranking-row${idx === 0 ? ' calc-dash-ranking-top' : ''}`}
                                        >
                                            <span>
                                                <strong>{idx + 1}.</strong> {area.area_name} ({area.events}{' '}
                                                {area.events === 1 ? 'baja' : 'bajas'})
                                            </span>
                                            <strong>{fmt(area.total_cost)}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="glass-card">
                                <h3>Desglose del costo del año</h3>
                                <ul className="calc-dash-desglose mt-md">
                                    <li>
                                        <span>Costos directos</span>
                                        <strong>{fmt(dashboard.yearly?.total_direct || 0)}</strong>
                                    </li>
                                    <li>
                                        <span>Productividad perdida</span>
                                        <strong>{fmt(dashboard.yearly?.total_productivity || 0)}</strong>
                                    </li>
                                    <li>
                                        <span>Fricción sistémica</span>
                                        <strong>{fmt(dashboard.yearly?.total_friction || 0)}</strong>
                                    </li>
                                    <li className="calc-dash-desglose-total">
                                        <span>Total del año</span>
                                        <strong>{fmt(dashboard.yearly?.total_cost || 0)}</strong>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-muted">Cargando dashboard financiero…</p>
                )
            ) : (
                <div className="config-tab stagger">
                    <div className="glass-card mb-lg cid-maestros-card">
                        <h3>Maestros CID — parámetros por nivel de puesto</h3>
                        <p className="text-sm text-muted mb-md">
                            Un solo conjunto de valores por nivel (operativo, técnico, supervisión). Al guardar se aplican a{' '}
                            <strong>todas las áreas</strong> de la empresa. Importes en COP; la brecha es el porcentaje de
                            productividad no recuperada durante la curva (0–100).
                        </p>
                        {cidLoading ? (
                            <div className="flex items-center gap-sm text-muted text-sm py-md">
                                <div className="spinner" style={{ width: 22, height: 22 }} />
                                Cargando maestros desde el servidor…
                            </div>
                        ) : areas.length === 0 ? (
                            <p className="text-sm text-muted">No hay áreas en la empresa. Configúralas en administración.</p>
                        ) : (
                            <div className="cid-maestros-scroll cid-maestros-scroll-level">
                                <table className="w-100 cid-maestros-table cid-maestros-level">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left cid-maestros-th cid-maestros-th-nivel">Nivel de puesto</th>
                                            <th className="text-right cid-maestros-th">Reclutamiento</th>
                                            <th className="text-right cid-maestros-th">Indemnización</th>
                                            <th className="text-right cid-maestros-th">EPP y herramientas</th>
                                            <th className="text-right cid-maestros-th">Horas administrativas</th>
                                            <th className="text-right cid-maestros-th">Curva (meses)</th>
                                            <th className="text-right cid-maestros-th">Brecha (%)</th>
                                            <th className="text-center cid-maestros-th cid-maestros-th-actions">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cidMatrix.map((row) => {
                                            const editing = cidEditingKey === row.key;
                                            const busy = cidSavingKey === row.key || cidDeletingKey === row.key;
                                            return (
                                                <tr key={row.key} className="border-b cid-maestros-row">
                                                    <td className="cid-maestro-cell cid-maestro-cell-nivel">
                                                        <strong>{CID_LEVEL_LABEL[row.positionLevel]}</strong>
                                                        {!row.persisted ? (
                                                            <span className="cid-maestro-badge">sin guardar</span>
                                                        ) : row.persistedAreas < row.areaIds.length ? (
                                                            <span className="cid-maestro-badge cid-maestro-badge-info">
                                                                en {row.persistedAreas}/{row.areaIds.length} áreas
                                                            </span>
                                                        ) : null}
                                                    </td>
                                                    {editing ? (
                                                        <>
                                                            {['recruitmentCost', 'severanceCost', 'ppeCost', 'adminHoursCost'].map((field) => (
                                                                <td key={field} className="text-right">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step="1"
                                                                        className="input cid-maestro-input cid-maestro-input-edit"
                                                                        value={row[field] === '' ? '' : row[field]}
                                                                        onChange={(e) =>
                                                                            updateCidRow(row.key, {
                                                                                [field]: e.target.value === '' ? '' : Number(e.target.value),
                                                                            })
                                                                        }
                                                                    />
                                                                </td>
                                                            ))}
                                                            <td className="text-right">
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.1"
                                                                    className="input cid-maestro-input cid-maestro-input-edit"
                                                                    value={row.learningCurveMonths === '' ? '' : row.learningCurveMonths}
                                                                    onChange={(e) =>
                                                                        updateCidRow(row.key, {
                                                                            learningCurveMonths:
                                                                                e.target.value === '' ? '' : Number(e.target.value),
                                                                        })
                                                                    }
                                                                />
                                                            </td>
                                                            <td className="text-right">
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    max="100"
                                                                    step="1"
                                                                    className="input cid-maestro-input cid-maestro-input-edit"
                                                                    value={row.gapPercent === '' ? '' : row.gapPercent}
                                                                    onChange={(e) =>
                                                                        updateCidRow(row.key, {
                                                                            gapPercent: e.target.value === '' ? '' : Number(e.target.value),
                                                                        })
                                                                    }
                                                                />
                                                            </td>
                                                            <td className="text-center cid-maestro-actions">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary btn-sm cid-maestro-btn"
                                                                    disabled={busy}
                                                                    onClick={() => saveCidRow(row)}
                                                                >
                                                                    {cidSavingKey === row.key ? '…' : 'Guardar'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline btn-sm cid-maestro-btn"
                                                                    disabled={busy}
                                                                    onClick={() => void cancelCidEdit()}
                                                                >
                                                                    Cancelar
                                                                </button>
                                                            </td>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <td className="text-right cid-maestro-cell cid-maestro-cell-num">{fmt(row.recruitmentCost)}</td>
                                                            <td className="text-right cid-maestro-cell cid-maestro-cell-num">{fmt(row.severanceCost)}</td>
                                                            <td className="text-right cid-maestro-cell cid-maestro-cell-num">{fmt(row.ppeCost)}</td>
                                                            <td className="text-right cid-maestro-cell cid-maestro-cell-num">{fmt(row.adminHoursCost)}</td>
                                                            <td className="text-right cid-maestro-cell cid-maestro-cell-num">
                                                                {row.learningCurveMonths === '' ? '—' : Number(row.learningCurveMonths).toFixed(1)}
                                                            </td>
                                                            <td className="text-right cid-maestro-cell cid-maestro-cell-num">
                                                                {row.gapPercent === '' ? '—' : `${row.gapPercent}%`}
                                                            </td>
                                                            <td className="text-center cid-maestro-actions">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-secondary btn-sm cid-maestro-btn"
                                                                    disabled={busy}
                                                                    onClick={() => setCidEditingKey(row.key)}
                                                                >
                                                                    Editar
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline btn-sm cid-maestro-btn cid-maestro-btn-danger"
                                                                    disabled={busy || !row.persisted}
                                                                    title={!row.persisted ? 'No hay registro en base de datos' : 'Eliminar maestro CID'}
                                                                    onClick={() => deleteCidRow(row)}
                                                                >
                                                                    {cidDeletingKey === row.key ? '…' : 'Eliminar'}
                                                                </button>
                                                            </td>
                                                        </>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="glass-card cid-mastery-card">
                            <h3>Factores de maestría</h3>
                            <p className="text-sm text-muted mb-lg">
                                Multiplicador para la <strong>fricción sistémica</strong> en el simulador (por nivel). Se guarda en
                                todas las áreas.
                            </p>

                            <table className="w-100 cid-mastery-table">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left cid-maestros-th">Nivel de puesto</th>
                                        <th className="text-right cid-maestros-th">Factor de maestría</th>
                                        <th className="text-center cid-maestros-th cid-maestros-th-actions">Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {CID_LEVEL_ORDER.map((level) => (
                                        <tr key={level} className="border-b cid-mastery-row">
                                            <td className="cid-maestro-cell-nivel">
                                                <strong>{CID_LEVEL_LABEL[level]}</strong>
                                            </td>
                                            <td className="text-right">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.05"
                                                    className="input cid-mastery-input"
                                                    value={masteryByLevel[level] === '' ? '' : masteryByLevel[level]}
                                                    onChange={(e) =>
                                                        setMasteryByLevel((prev) => ({
                                                            ...prev,
                                                            [level]: e.target.value === '' ? '' : Number(e.target.value),
                                                        }))
                                                    }
                                                />
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm cid-maestro-btn"
                                                    disabled={masterySavingLevel === level || areas.length === 0}
                                                    onClick={() => void saveMasteryLevel(level)}
                                                >
                                                    {masterySavingLevel === level ? 'Guardando…' : 'Guardar'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="cid-mastery-note mt-lg">
                                <p className="text-xs opacity-80" style={{ margin: 0, lineHeight: 1.45 }}>
                                    Indica cuántos salarios anuales equivale recuperar el conocimiento perdido al salir alguien de
                                    ese nivel. Referencia orientativa: operativo 0,5–0,7 · técnico 1,0–1,2 · supervisión 1,5–2,5.
                                </p>
                            </div>
                        </div>
                </div>
            ))}
        </div>
    );
}
